import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { apsEnums } from "../../shared/dto/aps.enums";
import * as angular from "angular";
import * as _ from "lodash";
import { SignalRHub } from "../hubs/signalr-hub";
import { Plant } from "../../models/navigation";
import { apsDisplayEnums } from "../../shared/dto/enums";
import LineCountsServiceName, {
  LineCounts, LineCountsService, LineEquipmentCounts, LineEquipmentGroup,
  LineEquipmentItem
} from "../../models/counts/lineCounts";
import { Endpoints } from "../../shared/config/constant.module";
import HubServiceName, { HubService } from "../hubs/hub.service";
import HttpServiceName, { HttpService } from "../http/http.service";
import ShopServiceName, { ShopService } from "../shops/shops.service";
import { apsCommon } from "../../shared/dto/aps.common";
import { ICountByDataPointId } from "../../models/counts/lineCounts";
import EquipmentServiceName, { EquipmentService } from "../equipment/equipment.service";
let requires = [HubServiceName, HttpServiceName, LineCountsServiceName, ShopServiceName, EquipmentServiceName];

export interface CountHub extends SignalRHub {
  joinGroup(shopId: string): Promise<any>;
  leaveGroup(shopId: string): Promise<any>;
}

export interface CountsService {
  getUsePercentGobsForPlantId(plantId: string): boolean | null;
  getUsePercentGobs(plant: apsViewModels.PlantNavigation): boolean;
  setUsePercentGobs(plantId: string, usePercentGobs: boolean): void;
  getLines(shopId: string): angular.IPromise<apsViewModels.ShopInspectionLines>;
  getCounts(shopId: string): angular.IPromise<{ [key: string]: apsViewModels.CountItem[] }>;
  getDetailedCounts(shopId: string, lineEquipmentId: string):
    angular.IPromise<apsViewModels.CountDetail>;
  getGroupDetailedCounts(shopId: string, types: apsEnums.LineEquipmentType[]):
    angular.IPromise<apsViewModels.CountDetail>;
  getLineCounts(
    shopId: string,
    plant: Plant,
    countsInterval: apsDisplayEnums.CountDisplayInterval,
    canDisable: boolean,
    isCountV2?: boolean,
    calcGroupByShears?: boolean):
    angular.IPromise<LineCounts>;
  getEquipmentCounts(
    shopId: string,
    lineCounts: LineCounts,
    equipment: LineEquipmentItem,
    countsInterval: apsDisplayEnums.CountDisplayInterval):
    angular.IPromise<LineEquipmentCounts>;
  getGroupEquipmentCounts(
    shopId: string,
    lineCounts: LineCounts,
    equipmentGroup: LineEquipmentGroup,
    countsInterval: apsDisplayEnums.CountDisplayInterval):
    angular.IPromise<LineEquipmentCounts>;
  getGroupRejectCounts(
    shopId: string,
    equipmentGroup: LineEquipmentGroup,
    startTime?: Date,
    endTime?: Date):
    angular.IPromise<apsViewModels.EquipmentRejects[]>;
  getDumpGateTimeCount(plantId: string, shopId: string, countsInterval: apsDisplayEnums.CountDisplayInterval):
    angular.IPromise<number>;
  refreshModel(shopId: string, model: LineCounts): angular.IPromise<any>;
  createHub(shopId: string, model: LineCounts): CountHub;
  getShopCountSummary(shopId: string, startDate: Date, endDate: Date, byShift: boolean):
    angular.IPromise<apsViewModels.ShopCountSummary[]>;
  getCavityEfficiency(shopId: string, startTime: Date, endTime: Date):
    angular.IPromise<apsViewModels.CavityCountSummary[]>;
  getCavityCorrelation(shopId: string, startTime: Date, endTime: Date):
    angular.IPromise<apsViewModels.CavityCorrelationCountSummary[]>;
  getEquipmentInconsistencies(
    shopId: string, equipmentId: string,
    lineConfig: apsViewModels.ShopInspectionLines): angular.IPromise<any>;
  getCavityCounts(
    shopId: string,
    types: apsEnums.LineEquipmentType[],
    startTime?: Date,
    endTime?: Date): angular.IPromise<apsViewModels.CavityDetail[]>;
  getByDatapointId(
    dataPointId: string,
    startDate: Date,
    endDate: Date,
    overrideInterval: string,
  ): angular.IPromise<ICountByDataPointId[]>;
  /*
   * get data point history list using mutiple data point ids
   */
  getByDatapointList(
    plantLocationId: string,
    shopId: string,
    dataPoints: string[],
    startDate: Date,
    endDate: Date,
    overrideInterval: string,
  ): angular.IPromise<ICountByDataPointId[]>;
}

export interface InconsistencyGroup {
  node: string;
  inconsistencies: InconsistencyGroupItem[];
}

export interface InconsistencyGroupItem {
  lineName: string;
  value: string;
}

export default angular
  .module('aps.services.counts', requires)
  .service('countsService', (
    httpService: HttpService,
    $q: angular.IQService,
    $timeout: angular.ITimeoutService,
    $window: angular.IWindowService,
    hubService: HubService,
    lineCountsService: LineCountsService,
    shopService: ShopService,
    equipmentService: EquipmentService): CountsService => {

    let lastRefresh: { [key: string]: number } = {};

    function getUsePercentGobs(plant: apsViewModels.PlantNavigation) {
      let userVal = getUsePercentGobsForPlantId(plant.id);
      if (userVal !== null) {
        return userVal;
      }

      return plant.countRejectPercent === apsEnums.CountRejectPercent.PercentOfGobsProduced;
    }

    function getUsePercentGobsForPlantId(plantId: string) {
      let userVal = $window.localStorage.getItem(plantId);
      if (userVal !== null) {
        return userVal === 'true';
      }
      return null;
    }

    function setUsePercentGobs(plantId: string, usePercentGobs: boolean) {
      $window.localStorage.setItem(plantId, usePercentGobs.toString());
    }

    function getCounts(shopId: string): angular.IPromise<{ [key: string]: apsViewModels.CountItem[] }> {
      return httpService.get(Endpoints.COUNTS, { shopId: shopId });
    }

    function getDetailedCounts(shopId: string, lineEquipmentId: string):
      angular.IPromise<apsViewModels.CountDetail> {
      return httpService.get(Endpoints.COUNTS_DETAIL, { shopId: shopId, lineEquipmentId: lineEquipmentId });
    }

    function getGroupDetailedCounts(shopId: string, types: apsEnums.LineEquipmentType[]):
      angular.IPromise<apsViewModels.GroupCountDetail> {
      return httpService.get(Endpoints.COUNTS_GROUP_DETAIL, { shopId: shopId, types: types });
    }

    function getLines(shopId: string): angular.IPromise<apsViewModels.ShopInspectionLines> {
      return httpService.get(Endpoints.LINE_CONFIG, { shopId: shopId });
    }

    function getLineCounts(
      shopId: string,
      plant: Plant,
      countsInterval: apsDisplayEnums.CountDisplayInterval,
      canDisable: boolean,
      isCountV2?: boolean,
      iscalcGroupByShears?: boolean):
      angular.IPromise<LineCounts> {
      return $q.all([getLines(shopId), getCounts(shopId), shopService.getShopConfiguration(shopId)]).then(results => {
        let lines: apsViewModels.ShopInspectionLines = results[0];
        let counts: { [key: string]: apsViewModels.CountItem[] } = results[1];
        let shopConfig = results[2];
        return lineCountsService.createLineCounts(lines, counts, plant, countsInterval, canDisable, shopConfig, isCountV2, iscalcGroupByShears);
      });
    }

    function getEquipmentCounts(
      shopId: string,
      lineCounts: LineCounts,
      equipment: LineEquipmentItem,
      countsInterval: apsDisplayEnums.CountDisplayInterval):
      angular.IPromise<LineEquipmentCounts> {
      return getDetailedCounts(shopId, equipment.id)
        .then(results => {
          return lineCountsService.createLineEquipmentCounts(results.tabs, lineCounts,
            equipment.detailDataPoints, results.counts, countsInterval, equipment, null);
        });
    }

    function getGroupEquipmentCounts(
      shopId: string,
      lineCounts: LineCounts,
      equipmentGroup: LineEquipmentGroup,
      countsInterval: apsDisplayEnums.CountDisplayInterval):
      angular.IPromise<LineEquipmentCounts> {
      return getGroupDetailedCounts(shopId, equipmentGroup.types)
        .then(results => {
          if (results.equipmentTabs && Object.keys(results.equipmentTabs).length > 0) {
            equipmentGroup.lineLineEquipmentTabs = results.equipmentTabs;
          }
          let details = [];
          for (let id in equipmentGroup.equipment) {
            let equipment = equipmentGroup.equipment[id];
            if (equipment.detailDataPoints.length > 0) {
              details.push(...equipment.detailDataPoints);
            }
          }
          return lineCountsService.createLineEquipmentCounts(results.tabs, lineCounts, details,
            results.counts, countsInterval, null, equipmentGroup);
        });
    }

    function getGroupRejectCounts(
      shopId: string,
      equipmentGroup: LineEquipmentGroup,
      startTime?: Date,
      endTime?: Date): angular.IPromise<apsViewModels.EquipmentRejects[]> {
      return httpService.get(Endpoints.COUNTS_GROUP_REJECTS, {
        shopId: shopId,
        names: equipmentGroup.names,
        startTime: startTime,
        endTime: endTime
      });
    }

    function getDumpGateTimeCount(
      plantId: string,
      shopId: string,
      countInterval: apsDisplayEnums.CountDisplayInterval):
      angular.IPromise<number> {
      return httpService.get(Endpoints.DUMPGATETIME_COUNT, {
        plantId,
        shopId,
        countInterval
      });
    }

    function refreshModel(shopId: string, model: LineCounts): angular.IPromise<any> {
      return $q.all([getCounts(shopId), shopService.getShopConfiguration(shopId)]).then(results => {
        let counts = results[0];
        let shopConfig = results[1];
        model.updateCounts(counts, shopConfig);
        if (model.currentDetailCounts) {
          if (model.currentDetailCounts.equipment) {
            getDetailedCounts(shopId, model.currentDetailCounts.equipment.id).then(equipmentCounts => {
              model.currentDetailCounts.updateCounts(equipmentCounts.counts, shopConfig);
            });
          } else {
            getGroupDetailedCounts(shopId, model.currentDetailCounts.group.types).then(equipmentCounts => {
              model.currentDetailCounts.updateCounts(equipmentCounts.counts, shopConfig);
            });
          }
        }
      });
    }

    function refreshModelIfEligible(endDate: Date, shopId: string, model: LineCounts) {
      let endTime = endDate.getTime();
      if (endTime <= lastRefresh[shopId]) {
        return;
      }

      lastRefresh[shopId] = endTime;
      $timeout(() => {
        refreshModel(shopId, model);
      }, 5000, false);
    }

    function onCountAdded(
      event: apsViewModels.ShopCountBatchAdded,
      model: LineCounts): void {
      $timeout(() => {
        model.handleCount(event);
      });
    }

    function onIntervalAdded(
      event: apsViewModels.ShopCountIntervalBatchAdded,
      model: LineCounts): void {
      $timeout(() => {
        model.handleInterval(event);
      });

      if (_.some(event.buckets, e => e.interval === apsEnums.CountInterval.Hour
        || (e.interval === apsEnums.CountInterval.TenMinutes && e.intervalEnd.getMinutes() !== 0))
      ) {
        refreshModelIfEligible(event.buckets[0].intervalEnd, event.shopId, model);
      }
    }

    function onEquipmentHealthUpdated(
      event: apsViewModels.EquipmentHealthUpdated,
      model: LineCounts):
      void {
      $timeout(() => {
        model.handleEquipmentHealthUpdate(event);
      });
    }

    function inconsistentEquipmentConfigurationChanged(
      event: apsViewModels.InconsistentEquipmentConfigurationChanged, model: LineCounts): void {
      $timeout(() => {
        model.handleInconsistencyChanged(event);
      });
    }

    function createHub(shopId: string, model: LineCounts): CountHub {
      lastRefresh[shopId] = new Date().getTime();

      return hubService.createHub<CountHub>('count', {
        listeners: {
          countAdded: (event: apsViewModels.ShopCountBatchAdded) => {
            if (onCountAdded && event.shopId === shopId) {
              onCountAdded(event, model);
            }
          },
          intervalAdded: (event: apsViewModels.ShopCountIntervalBatchAdded) => {
            if (onIntervalAdded && event.shopId === shopId) {
              onIntervalAdded(event, model);
            }
          },
          equipmentHealthUpdated: (event: apsViewModels.EquipmentHealthUpdated) => {
            onEquipmentHealthUpdated(event, model);
          },
          inconsistentEquipmentConfigurationChanged:
            (event: apsViewModels.InconsistentEquipmentConfigurationChanged) => {
              inconsistentEquipmentConfigurationChanged(event, model);
            }
        },
        methods: ['leaveGroup', 'joinGroup'],
        postConnect: (hub: CountHub) => {
          hub.invokeWithRetry(() => hub.joinGroup(shopId));
        }
      });
    }

    function getEquipmentInconsistencies(
      shopId: string,
      equipmentId: string,
      lineConfig: apsViewModels.ShopInspectionLines):
      angular.IPromise<InconsistencyGroup[]> {

      return equipmentService.getShop(shopId).then(equipmentResult => {
        let equipmentType = _.find(equipmentResult, { id: equipmentId }).type;
        return httpService.get<apsCommon.InconsistentEquipmentConfigurationItem[]>(
          Endpoints.LINE_CONFIG_EQUIPMENT_INCONSISTENCIES, {
          shopId: shopId, equipmentType: equipmentType
        })
          .then(result => {
            let inconsistencies: InconsistencyGroup[] = [];
            if (result) {
              (result).forEach(item => {
                let inconsistency: InconsistencyGroup = <InconsistencyGroup>{};
                inconsistency.inconsistencies = [];

                let nodePath: string[] = [];
                while (item.node.parent !== null) {
                  nodePath.push(`${item.node.name}${(item.node.id ? ` ${item.node.id}` : '')}`);
                  item.node = item.node.parent;
                }

                inconsistency.node = `${nodePath.length > 0
                  ? nodePath.reverse().toString().replace(',', ' ')
                  : ''} ${item.attribute}`;

                for (let detail = 0; detail < item.detail.length; detail++) {
                  let detailItem = item.detail[detail];
                  let detailEquipment = _.find(equipmentResult, { id: detailItem.equipmentId });
                  if (!detailEquipment) {
                    detailEquipment = <apsViewModels.EquipmentDetail>{
                      id: detailItem.equipmentId,
                      name: null
                    };
                  }

                  let lineName = null;
                  for (let line = 0; line < lineConfig.lines.length; line++) {
                    let lineItem = lineConfig.lines[line];
                    var equipmentInstance = _.find(lineItem.equipment, { equipmentId: detailItem.equipmentId });
                    if (equipmentInstance) {
                      lineName = `${equipmentInstance.name}-${lineItem.name}`;
                      break;
                    }
                  }

                  let isRemoved = false;
                  if (!lineName) {
                    lineName = '(Equipment Removed)';
                    isRemoved = true;
                  }

                  inconsistency.inconsistencies.push({
                    lineName: lineName,
                    value: detailItem.value
                      ? detailItem.value
                      : isRemoved
                        ? null
                        : 'Missing'
                  });
                }

                inconsistencies.push(inconsistency);
              });
            }

            return inconsistencies;
          });
      });
    }

    function getShopCountSummary(shopId: string, startDate: Date, endDate: Date, byShift: boolean):
      angular.IPromise<apsViewModels.ShopCountSummary[]> {
      if(byShift) {
        return httpService.get(Endpoints.SHOP_COUNT_SUMMARY_DEFECTS, {
          shopId: shopId,
          startDate: startDate,
          endDate: endDate,
          byShift: byShift
        });
      }
      return httpService.get(Endpoints.SHOP_COUNT_SUMMARY, {
        shopId: shopId,
        startDate: startDate,
        endDate: endDate
      });
    }

    function getCavityEfficiency(shopId: string, startTime: Date, endTime: Date):
      angular.IPromise<apsViewModels.CavityCountSummary[]> {
      return httpService.get(Endpoints.CAVITY_EFFICIENCY, { shopId: shopId, startTime: startTime, endTime: endTime });
    }

    function getCavityCorrelation(shopId: string, startTime: Date, endTime: Date):
      angular.IPromise<apsViewModels.CavityCorrelationCountSummary[]> {
      return httpService.get(Endpoints.CAVITY_CORRELATION, { shopId: shopId, startTime: startTime, endTime: endTime });
    }

    function getCavityCounts(
      shopId: string, types: apsEnums.LineEquipmentType[],
      startTime?: Date, endTime?: Date):
      angular.IPromise<apsViewModels.CavityDetail[]> {
      return httpService.get(Endpoints.COUNTS_CAVITY, {
        shopId: shopId,
        types: types,
        startTime: startTime,
        endTime: endTime
      });
    }

    function getByDatapointId(
      dataPointId: string,
      startDate: Date,
      endDate: Date,
      overrideInterval: string,
    ): angular.IPromise<ICountByDataPointId[]> {
      return httpService.get(Endpoints.COUNTS_DATA_POINT_HISTORY, {
        dataPointId: dataPointId,
        startDate: startDate,
        endDate: endDate,
        overrideInterval: overrideInterval
      });
    }

    /*
     * get data point history list
     */
    function getByDatapointList(
      plantLocationId: string,
      shopId: string,
      dataPoints: string[],
      startDate: Date,
      endDate: Date,
      overrideInterval: string,
    ): angular.IPromise<ICountByDataPointId[]> {
      return httpService.get(Endpoints.COUNTS_DATA_POINT_LIST, {
        plantLocationId: plantLocationId,
        shopId: shopId,
        dataPoints: dataPoints,
        startDate: startDate,
        endDate: endDate,
        overrideInterval: overrideInterval
      });
    }

    return {
      getCounts: getCounts,
      getDetailedCounts: getDetailedCounts,
      getGroupDetailedCounts: getGroupDetailedCounts,
      getLineCounts: getLineCounts,
      getEquipmentCounts: getEquipmentCounts,
      getGroupEquipmentCounts: getGroupEquipmentCounts,
      getLines: getLines,
      getDumpGateTimeCount:getDumpGateTimeCount,
      refreshModel: refreshModel,
      createHub: createHub,
      getEquipmentInconsistencies: getEquipmentInconsistencies,
      getShopCountSummary: getShopCountSummary,
      getCavityEfficiency: getCavityEfficiency,
      getCavityCorrelation: getCavityCorrelation,
      getGroupRejectCounts: getGroupRejectCounts,
      getCavityCounts: getCavityCounts,
      getByDatapointId: getByDatapointId,
      getByDatapointList: getByDatapointList,
      getUsePercentGobsForPlantId: getUsePercentGobsForPlantId,
      getUsePercentGobs: getUsePercentGobs,
      setUsePercentGobs: setUsePercentGobs
    };
  })
  .name;


// WEBPACK FOOTER //
// ./src/app/services/counts/counts.service.ts